<template>
  <Drawer direction="right" :open="open" @update:open="$emit('update:open', $event)">
    <DrawerContent class="sm:max-w-xl">
      <DrawerHeader class="text-left">
        <DrawerTitle>Update user tiers</DrawerTitle>
        <DrawerDescription>Upload user tiers via CSV and review previous uploads.</DrawerDescription>
      </DrawerHeader>

      <div class="flex flex-col gap-4 overflow-y-auto px-4 flex-1">
        <!-- Upload -->
        <div class="space-y-3 rounded-lg border border-border p-3">
          <div class="space-y-1">
            <label class="text-sm font-medium" for="upload-csv">CSV file</label>
            <input
              id="upload-csv"
              type="file"
              accept=".csv"
              ref="fileInput"
              @change="uploadFile"
              class="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-2 file:text-sm file:font-medium"
            />
          </div>
          <div class="flex items-center gap-2">
            <Button size="sm" variant="outline" @click="downloadCSV()">
              <Download class="w-4 h-4" />Download template
            </Button>
            <Button size="sm" :disabled="uploadCSV == undefined" @click="uploadTransactions()">
              <Upload class="w-4 h-4" />Upload
            </Button>
          </div>
        </div>

        <!-- History -->
        <div>
          <p class="text-sm font-medium mb-2">Uploads history</p>
          <DataTable
            :headers="headersTable1"
            :items="fileuploads"
            :loading="loading"
            :error="error"
            @retry="getFileUploads(serverOptions.page - 1, serverOptions.rowsPerPage)"
            v-model:server-options="serverOptions"
            :server-items-length="serverItemsLength"
            :hide-footer="true"
          >
            <template #item-id="fileData">
              <CopyId v-if="fileData.id" :value="fileData.id" :max-width="120" />
              <span v-else class="text-muted-foreground">—</span>
            </template>
            <template #item-fileName="fileData">
              <span v-if="fileData.fileName" class="text-sm font-medium truncate" :title="fileData.fileName">{{ fileData.fileName }}</span>
              <span v-else class="text-muted-foreground">—</span>
            </template>
            <template #item-createdOn="fileData">
              <span class="text-sm text-muted-foreground whitespace-nowrap">{{ fileData.createdOn ? niceDateTime(fileData.createdOn) : '—' }}</span>
            </template>
            <template #item-fileStatus="fileData">
              <Badge v-if="fileData.fileStatus" :variant="fileStatusVariant(fileData.fileStatus)">{{ fileData.fileStatus }}</Badge>
              <span v-else class="text-muted-foreground">—</span>
            </template>
          </DataTable>
        </div>
      </div>

      <DrawerFooter class="flex-row justify-end">
        <Button variant="outline" @click="$emit('update:open', false)">Close</Button>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
</template>

<script>
import { DateTime } from 'luxon'
import {
  Button,
  Badge,
  CopyId,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from '@boogi/ui'
import { Upload, Download } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'EndUserUploadDrawer',
  components: {
    DataTable,
    Button,
    Badge,
    CopyId,
    Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
    Upload, Download,
  },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['update:open'],

  data() {
    return {
      fileuploads: [],
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      uploadCSV: undefined,
      serverItemsLength: undefined,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Id', value: 'id' },
        { text: 'File name', value: 'fileName' },
        { text: 'Created On', value: 'createdOn' },
        { text: 'Status', value: 'fileStatus' },
      ],
    }
  },
  methods: {
    downloadCSV() {
      // Define the data
      const headers = ['END_USER_ID', 'CUSTOMER_TIER_ID', 'ACTION']
      const rows = [['user-1234', 'tier-1234', 'ADD / REMOVE']]

      // Convert to CSV format
      const csvContent = [headers, ...rows]
        .map((row) => row.map((value) => `"${value}"`).join(','))
        .join('\n')

      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', 'user_tiers.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    uploadFile: function () {
      this.uploadCSV = event.target.files[0]
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    fileStatusVariant(status) {
      const s = String(status || '').toUpperCase()
      if (['COMPLETED', 'COMPLETE', 'SUCCESS', 'PROCESSED', 'DONE'].includes(s)) return 'success'
      if (['PENDING', 'PROCESSING', 'IN_PROGRESS'].includes(s)) return 'warning'
      if (['FAILED', 'ERROR', 'REJECTED'].includes(s)) return 'destructive'
      return 'secondary'
    },

    async uploadTransactions() {
      const formData = new FormData()
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      formData.append('multipartFile', this.uploadCSV)

      try {
        const token = localStorage.getItem('token')
        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/csv/user/tier-upload`,
          {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.uploadCSV = undefined
          if (this.$refs.fileInput) this.$refs.fileInput.value = ''
          this.getFileUploads(0, 25)
        }
      } catch (error) {
        return
      }
    },

    async getFileUploads(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/csv/user/tier-upload?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.fileuploads = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    // Refresh history whenever the drawer is opened.
    open(isOpen) {
      if (isOpen) this.getFileUploads(0, 25)
    },
    serverOptions: {
      deep: true,
      handler(val) {
        this.getFileUploads(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
